/**
 * HandoverForm — Intake / Outbound Felder-Editor mit Draft-State + expliziter Save-Button.
 * Quelle: §10.18 Extraktion aus project-detail.tsx (§10.12-Pattern).
 *
 * Hidden Keys aus `initial` werden beim Save mitgepatcht, damit ausgeblendete
 * Felder (z.B. presales_notes) nicht versehentlich aus dem JSONB rausfallen.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { Card } from './card.js';

function BlockShell({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? <>{children}</> : <Card>{children}</Card>;
}

/**
 * Die Felder der Ausgangs-Übergabe (`handover_ready_fields`) — EINE Quelle für alle
 * Aufrufer: Übergabe-Ansicht (`handover.tsx`), Projektdetail-Tab „Übergabe"
 * (`project-detail.tsx`) und KFML-Worklist (`kfml-worklist.tsx`).
 *
 * Lag vorher dreimal wortgleich in diesen Dateien. Ein fünftes Feld hätte an drei Stellen
 * nachgezogen werden müssen — genau die Drift, die diese Konstante verhindert. Liegt hier,
 * weil `HandoverForm` der einzige Konsument ist (Prop `fields`).
 */
export const OUTBOUND_FIELDS = [
  'restbudget',
  'folgethemen',
  'debriefing',
  'uebergabe_an_service',
  // #119: befuellen setzt automatisch den KFML-Worklist-Haken (kein Pflichtfeld).
  'kfml_notiz',
] as const;

export type OutboundFieldKey = (typeof OUTBOUND_FIELDS)[number];

export function HandoverForm<K extends string>({
  kind,
  title,
  hint,
  fields,
  required,
  initial,
  projectId,
  canEdit,
  isReader,
  onSaved,
  embedded = false,
}: {
  kind: 'intake' | 'outbound';
  title: string;
  hint: string;
  fields: K[];
  required: Set<string>;
  initial: Record<string, string | undefined>;
  projectId: string;
  canEdit: boolean;
  isReader: boolean;
  onSaved?: () => void;
  embedded?: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const obj: Record<string, string> = {};
    for (const f of fields) obj[f] = initial[f] ?? '';
    return obj;
  });

  const dirty = fields.some((f) => (draft[f] ?? '') !== (initial[f] ?? ''));

  const save = useMutation({
    mutationFn: () => {
      const cleaned: Record<string, string | undefined> = {};
      for (const [k, v] of Object.entries(initial)) {
        if (!fields.includes(k as K) && v) cleaned[k] = v;
      }
      for (const f of fields) {
        const v = draft[f]?.trim();
        if (v) cleaned[f] = v;
      }
      const patch =
        kind === 'intake'
          ? { intake_handover_fields: cleaned }
          : { handover_ready_fields: cleaned };
      return api.patchProject(projectId, patch as never);
    },
    onSuccess: () => {
      toast.success(t('v2.detail.handover_saved', 'Übergabe gespeichert'));
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      onSaved?.();
    },
    onError: (err) =>
      toast.error(
        t('v2.detail.handover_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <BlockShell embedded={embedded}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {title}
        </h3>
        {required.size > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.1em]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.detail.handover_required_marker', '* = pflicht in dieser Phase')}
          </span>
        )}
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--ink-3)' }}>
        {hint}
      </p>
      <div className={embedded ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
        {fields.map((f) => {
          const isReq = required.has(f);
          const labelKey = `v2.detail.handover_label_${f}`;
          const label = t(labelKey, fieldFallbackLabel(f));
          const missing = isReq && !draft[f]?.trim();
          // projectId mit rein: die KFML-Worklist kann mehrere HandoverForm-Instanzen
          // gleichzeitig rendern (eine je Projektkarte) — ohne projectId kollidieren die
          // IDs (ungueltiges HTML, `getByLabelText` traefe mehrere Treffer).
          const fieldId = `handover-${kind}-${projectId}-${f}`;
          return (
            <div key={f}>
              <label
                htmlFor={fieldId}
                className="block text-xs mb-1"
                style={{ color: 'var(--ink-2)' }}
              >
                {label}
                {isReq && <span style={{ color: 'var(--danger)' }}> *</span>}
              </label>
              <textarea
                id={fieldId}
                value={draft[f] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                disabled={!canEdit}
                title={isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined}
                rows={
                  f === 'presales_notes' ||
                  f === 'debriefing' ||
                  f === 'intake_meeting_notes' ||
                  f === 'folgethemen'
                    ? 3
                    : 2
                }
                className="w-full text-sm px-3 py-2 rounded-lg outline-none transition-colors"
                style={{
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: `1px solid ${missing ? 'var(--danger)' : 'var(--line-soft)'}`,
                  opacity: canEdit ? 1 : 0.6,
                  cursor: canEdit ? 'text' : 'not-allowed',
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        {dirty && (
          <button
            type="button"
            onClick={() => {
              const obj: Record<string, string> = {};
              for (const f of fields) obj[f] = initial[f] ?? '';
              setDraft(obj);
            }}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
            }}
          >
            {t('v2.modal.cancel', 'Abbrechen')}
          </button>
        )}
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!canEdit || !dirty || save.isPending}
          title={isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !canEdit || !dirty || save.isPending ? 0.5 : 1,
            cursor: !canEdit ? 'not-allowed' : 'pointer',
          }}
        >
          {save.isPending
            ? t('v2.detail.handover_saving', 'Speichert…')
            : t('common.save', 'Speichern')}
        </button>
      </div>
    </BlockShell>
  );
}

function fieldFallbackLabel(key: string): string {
  switch (key) {
    case 'presales_notes':
      return 'Presales-Notizen';
    case 'angebot_link':
      return 'Angebot-Link';
    case 'angebot_besonderheiten':
      return 'Besonderheiten am Angebot';
    case 'intake_meeting_notes':
      return 'Intake-Meeting-Notizen';
    case 'restbudget':
      return 'Restbudget';
    case 'folgethemen':
      return 'Folgethemen';
    case 'debriefing':
      return 'Debriefing';
    case 'uebergabe_an_service':
      return 'Übergabe an Service';
    case 'kfml_notiz':
      return 'Notiz kfm. Leitung';
    default:
      return key;
  }
}
